import { createContext, useContext, useReducer, Dispatch, useMemo } from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { TDocument } from '../../../../apis/documents/documents.interfaces';
import {
  IStudentListQueryParams,
  TStudentDetailsSimplified,
} from '../../../../apis/students/students.interfaces';
import { useStudentDocumentsHook } from '../hooks/useStudentDocumentsHook';
import { useStudentListHook } from '../hooks/useStudentListHook';
import { useStudentFiltersState } from './studentFilters.context';

export enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_STUDENT_DOCUMENTS = 'UPDATE_STUDENT_DOCUMENTS',
  UPDATE_DOCUMENTS_LIST = 'UPDATE_DOCUMENTS_LIST',
  SET_IS_MANAGE_DOCUMENT_OPEN = 'SET_IS_MANAGE_DOCUMENT_OPEN',
  SET_IS_DOCUMENT_LOADING = 'SET_IS_DOCUMENT_LOADING',
  SET_IS_DOCUMENT_ERROR = 'SET_IS_DOCUMENT_ERROR',
  SET_DOCUMENT_ERROR_MESSAGE = 'SET_DOCUMENT_ERROR_MESSAGE',
  SET_SELECTED_DOCUMENT = 'SET_SELECTED_DOCUMENT',
  SET_SELECTED_DOCUMENT_ID = 'SET_SELECTED_DOCUMENT_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  ADD_DOCUMENT = 'ADD_DOCUMENT',
  UPDATE_DOCUMENT = 'UPDATE_DOCUMENT',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type TDocumentsAppState = {
  documentsList: TDocument[];
  isManageDocumentOpen: boolean;
  isDocumentLoading: boolean;
  isDocumentError: boolean;
  documentErrorMessage: string;
  selectedDocument: TDocument | null;
  selectedDocumentId: string;
  isCreateMode: boolean;
  isEditMode: boolean;
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
};

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.UPDATE_STUDENT_DOCUMENTS; payload: TDocument[] }
  | { type: ACTION_TYPE.UPDATE_DOCUMENTS_LIST; payload: TDocument[] }
  | { type: ACTION_TYPE.SET_IS_MANAGE_DOCUMENT_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_DOCUMENT_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_DOCUMENT_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE; payload: string }
  | { type: ACTION_TYPE.SET_SELECTED_DOCUMENT; payload: TDocument | null }
  | { type: ACTION_TYPE.SET_SELECTED_DOCUMENT_ID; payload: string }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.ADD_DOCUMENT; payload: TDocument }
  | { type: ACTION_TYPE.UPDATE_DOCUMENT; payload: TDocument }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialStudentDocumentsState: TDocumentsAppState = {
  documentsList: [],
  isManageDocumentOpen: false,
  isDocumentLoading: false,
  isDocumentError: false,
  documentErrorMessage: '',
  selectedDocument: null,
  selectedDocumentId: '',
  isCreateMode: false,
  isEditMode: false,
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
};

const getSelectedFilterValue = (value?: string): string | undefined => {
  const selectedValue = value?.trim();
  return selectedValue && selectedValue !== 'select' ? selectedValue : undefined;
};

type TDocumentsState = {
  studentDocumentsAppState: TDocumentsAppState;
  studentDocumentRecords: TDocument[];
  simplifiedStudentObject: Record<string, TStudentDetailsSimplified>;
};

const StudentDocumentsContext = createContext<TDocumentsState | undefined>(
  undefined
);

const StudentDocumentsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const studentDocumentsReducer = (
  state: TDocumentsAppState,
  action: Action
): TDocumentsAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        userParams: { ...state.userParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_STUDENT_DOCUMENTS:
      return { ...state, documentsList: action.payload };
    case ACTION_TYPE.UPDATE_DOCUMENTS_LIST:
      return { ...state, documentsList: action.payload };
    case ACTION_TYPE.SET_IS_MANAGE_DOCUMENT_OPEN:
      return { ...state, isManageDocumentOpen: action.payload };
    case ACTION_TYPE.SET_IS_DOCUMENT_LOADING:
      return { ...state, isDocumentLoading: action.payload };
    case ACTION_TYPE.SET_IS_DOCUMENT_ERROR:
      return { ...state, isDocumentError: action.payload };
    case ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE:
      return { ...state, documentErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_DOCUMENT:
      return { ...state, selectedDocument: action.payload };
    case ACTION_TYPE.SET_SELECTED_DOCUMENT_ID:
      return { ...state, selectedDocumentId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload, isEditMode: false };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload, isCreateMode: false };
    case ACTION_TYPE.ADD_DOCUMENT:
      return {
        ...state,
        documentsList: [...state.documentsList, action.payload],
      };
    case ACTION_TYPE.UPDATE_DOCUMENT:
      return {
        ...state,
        documentsList: state.documentsList.map((doc) =>
          doc.documentId === action.payload.documentId ? action.payload : doc
        ),
        selectedDocument:
          state.selectedDocument?.documentId === action.payload.documentId
            ? action.payload
            : state.selectedDocument,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    default:
      return state;
  }
};

export const StudentDocumentsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const filters = useStudentFiltersState();
  const selectedGrade = getSelectedFilterValue(filters.grade);
  const selectedSection = getSelectedFilterValue(filters.section);
  const studentListParams = useMemo<IStudentListQueryParams>(() => {
    const params: IStudentListQueryParams = {
      page: 1,
      limit: 100,
    };
    if (selectedGrade) {
      params.classId = selectedGrade;
    }
    if (selectedSection) {
      params.sectionId = selectedSection;
    }
    return params;
  }, [selectedGrade, selectedSection]);
  const { data: studentDocumentsData = [] } = useStudentDocumentsHook({
    page: 1,
    limit: 100,
  });
  const { data: studentListData } = useStudentListHook(studentListParams, {
    enabled: !!selectedGrade,
  });

  const [state, dispatch] = useReducer(studentDocumentsReducer, {
    ...initialStudentDocumentsState,
    documentsList: studentDocumentsData,
  });

  const simplifiedStudentObject = useMemo<Record<string, TStudentDetailsSimplified>>(
    () => {
      if (!studentListData?.data) {
        return {};
      }
      return studentListData.data.reduce(
        (
          acc: Record<string, TStudentDetailsSimplified>,
          student
        ) => {
          acc[student.id] = {
            id: student.id,
            basicDetails: {
              firstName: student.basicDetails.firstName,
              middleName: student.basicDetails.middleName,
              lastName: student.basicDetails.lastName,
              gender: student.basicDetails.gender,
            },
            grade: student.grade,
            section: student.section,
          };
          return acc;
        },
        {} as Record<string, TStudentDetailsSimplified>
      );
    },
    [studentListData]
  );

  const contextValue: TDocumentsState = {
    studentDocumentsAppState: state,
    studentDocumentRecords: state.documentsList,
    simplifiedStudentObject,
  };

  return (
    <StudentDocumentsContext.Provider value={contextValue}>
      <StudentDocumentsDispatchContext.Provider value={dispatch}>
        {children}
      </StudentDocumentsDispatchContext.Provider>
    </StudentDocumentsContext.Provider>
  );
};

export const useStudentDocumentsState = () => {
  const context = useContext(StudentDocumentsContext);
  if (context === undefined) {
    throw new Error(
      'useStudentDocumentsState must be used within a StudentDocumentsProvider'
    );
  }
  return context;
};

export const useStudentDocumentsDispatch = () => {
  const context = useContext(StudentDocumentsDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useStudentDocumentsDispatch must be used within a StudentDocumentsProvider'
    );
  }
  return context;
};

export const useStudentDocumentsActions = () => {
  const dispatch = useStudentDocumentsDispatch();
  if (dispatch === undefined) {
    throw new Error(
      'useStudentDocumentsActions must be used within a StudentDocumentsProvider'
    );
  }

  const setIsManageDocumentOpen = (isOpen: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_MANAGE_DOCUMENT_OPEN,
      payload: isOpen,
    });
  };

  const setIsDocumentLoading = (isLoading: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_DOCUMENT_LOADING, payload: isLoading });
  };

  const setIsDocumentError = (isError: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_DOCUMENT_ERROR, payload: isError });
  };

  const setDocumentErrorMessage = (errorMessage: string) => {
    dispatch({
      type: ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedDocument = (document: TDocument | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_DOCUMENT, payload: document });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  const addDocument = (document: TDocument) => {
    dispatch({ type: ACTION_TYPE.ADD_DOCUMENT, payload: document });
  };

  const updateDocument = (document: TDocument) => {
    dispatch({ type: ACTION_TYPE.UPDATE_DOCUMENT, payload: document });
  };

  const setSelectedDocumentId = (documentId: string) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_DOCUMENT_ID,
      payload: documentId,
    });
  };

  return {
    setIsManageDocumentOpen,
    setIsDocumentLoading,
    setIsDocumentError,
    setDocumentErrorMessage,
    setSelectedDocument,
    setSelectedDocumentId,
    setIsCreateMode,
    setIsEditMode,
    addDocument,
    updateDocument,
  };
};
